import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import { KeyRound, Radio } from "lucide-react";
import { api } from "@/lib/apiClient";
import {
  AccessKeyFields,
  EMPTY_ACCESS_KEY_FORM,
  accessKeysFromBooking,
  accessKeysToApi,
  type AccessKeyFormValues,
} from "@/components/bookings/AccessKeyFields";
import {
  CheckInPitchSelectionStep,
  type CheckInPitchAssignmentMode,
  type CheckInPitchChoice,
} from "@/components/bookings/CheckInPitchSelectionStep";
import { CheckInPitchSummaryMap } from "@/components/bookings/CheckInPitchSummaryMap";
import { GuestStaySummary } from "@/components/bookings/GuestStaySummary";
import { confirmGuestTabletPayment } from "@/lib/guestTabletApi";
import { useGuestTabletStaffSession } from "@/lib/useGuestTabletStaffSession";
import { AddInvoiceLineModal } from "@/components/invoices/AddInvoiceLineModal";
import { InvoiceLinesEditor } from "@/components/invoices/InvoiceLinesEditor";
import { InvoicePaymentsSection } from "@/components/invoices/InvoicePaymentsSection";
import { InvoiceQuickPaymentPanel } from "@/components/invoices/InvoiceQuickPaymentPanel";
import { InvoiceTypeBadge } from "@/components/invoices/InvoiceTypeBadge";
import { canAddInvoiceLine } from "@/lib/invoiceLineEdit";
import {
  clearGuestTabletAfterCheckIn,
  syncGuestTabletCheckIn,
} from "@/lib/guestTabletCheckInSync";
import { isInvoiceFinalized } from "@/lib/invoiceStatus";
import { formatCurrency } from "@/lib/money";
import { canWriteInvoice } from "@/lib/permissions";
import type { TouristPaymentMoment } from "@/lib/touristPayment";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { StatusBadge } from "@/components/ui/StatusBadge";
import type { Booking, Guest, Invoice, Pitch } from "@/types/camping";

type FlowStep = "keys" | "pitch" | "summary" | "invoice";

const STEP_DEFS: { id: FlowStep; label: string }[] = [
  { id: "keys", label: "Zugang" },
  { id: "pitch", label: "Platz" },
  { id: "summary", label: "Übersicht" },
  { id: "invoice", label: "Rechnung" },
];

function openAmountNumber(invoice: Invoice): number {
  const raw = invoice.open_amount ?? invoice.total_amount;
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

export interface CheckInFlowModalProps {
  open: boolean;
  booking: Booking | null;
  guest: Guest | null;
  pitch: Pitch | null;
  userGroups: string[];
  /** Gäste-Tablet-Arbeitsplatz (URL-Code), z. B. reception-1 */
  guestTabletWorkstationCode?: string | null;
  onClose: () => void;
  onCompleted: (booking: Booking) => void;
  /** Check-in abgeschlossen, Rechnungsentwurf aber nicht fertiggestellt. */
  onDraftAborted?: (invoiceId: string, booking: Booking) => void;
}

export function CheckInFlowModal({
  open,
  booking,
  guest,
  pitch,
  userGroups,
  guestTabletWorkstationCode = null,
  onClose,
  onCompleted,
  onDraftAborted,
}: CheckInFlowModalProps) {
  const canInvoice = canWriteInvoice(userGroups);
  const transponderRef = useRef<HTMLInputElement>(null);
  const tabletStartSyncedRef = useRef<string | null>(null);

  const [step, setStep] = useState<FlowStep>("keys");
  const [accessKeys, setAccessKeys] = useState<AccessKeyFormValues>(EMPTY_ACCESS_KEY_FORM);
  const [selectedPitchIds, setSelectedPitchIds] = useState<string[]>([]);
  const [assignedPitchId, setAssignedPitchId] = useState<string | null>(null);
  const [guestChosenPitchId, setGuestChosenPitchId] = useState<string | null>(null);
  const [waitingForGuestPitch, setWaitingForGuestPitch] = useState(false);
  const [pitchAssignmentMode, setPitchAssignmentMode] =
    useState<CheckInPitchAssignmentMode>("reception");
  const [pitchChoices, setPitchChoices] = useState<CheckInPitchChoice[]>([]);
  const [receptionPickId, setReceptionPickId] = useState<string | null>(null);
  const [receptionCheckIn, setReceptionCheckIn] = useState(false);
  const [pitchSubmit, setPitchSubmit] = useState<"reception" | "guest" | null>(null);
  const guestPitchBaselineRef = useRef<string | null>(null);
  const [checkedInBooking, setCheckedInBooking] = useState<Booking | null>(null);
  const [localBooking, setLocalBooking] = useState<Booking | null>(null);
  const [invoiceId, setInvoiceId] = useState<string | null>(null);
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [touristMoment, setTouristMoment] = useState<TouristPaymentMoment>("check_out");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invoiceAction, setInvoiceAction] = useState<string | null>(null);
  const [addLineOpen, setAddLineOpen] = useState(false);
  const [confirmingTabletPayment, setConfirmingTabletPayment] = useState(false);
  const [bypassGuestDataConfirmation, setBypassGuestDataConfirmation] = useState(false);

  const tabletSession = useGuestTabletStaffSession(guestTabletWorkstationCode, {
    enabled: open,
  });
  const pendingTabletPayment = tabletSession.pendingPayment;
  const guestDataConfirmed = tabletSession.guestDataConfirmed;

  const expectsInvoiceStep =
    guest?.guest_type === "tourist" && touristMoment === "check_in";

  const visibleSteps = useMemo(
    () =>
      expectsInvoiceStep || invoiceId
        ? STEP_DEFS
        : STEP_DEFS.filter((s) => s.id !== "invoice"),
    [expectsInvoiceStep, invoiceId],
  );

  const reset = useCallback(() => {
    setStep("keys");
    setAccessKeys(EMPTY_ACCESS_KEY_FORM);
    setSelectedPitchIds([]);
    setAssignedPitchId(null);
    setGuestChosenPitchId(null);
    guestPitchBaselineRef.current = null;
    setWaitingForGuestPitch(false);
    setPitchAssignmentMode("reception");
    setPitchChoices([]);
    setReceptionPickId(null);
    setReceptionCheckIn(false);
    setPitchSubmit(null);
    setCheckedInBooking(null);
    setLocalBooking(null);
    setInvoiceId(null);
    setInvoice(null);
    setError(null);
    setInvoiceAction(null);
    setAddLineOpen(false);
    setConfirmingTabletPayment(false);
    setBypassGuestDataConfirmation(false);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!open) {
      void clearGuestTabletAfterCheckIn(guestTabletWorkstationCode);
      reset();
      tabletStartSyncedRef.current = null;
      return;
    }
    if (booking) {
      setLocalBooking(booking);
      setAccessKeys(accessKeysFromBooking(booking));
      if (booking.pitch_id) {
        setSelectedPitchIds([booking.pitch_id]);
      }
    }
    api
      .get<{ tourist_payment?: { moment?: TouristPaymentMoment } }>("/api/v1/settings")
      .then((s) => setTouristMoment(s.tourist_payment?.moment ?? "check_out"))
      .catch(() => {
        /* Standard: keine Vorkasse-Vorhersage */
      });
  }, [open, booking?.id, guestTabletWorkstationCode, reset]);

  useEffect(() => {
    if (!open || !booking) return;
    const syncKey = `${booking.id}:${guestTabletWorkstationCode ?? ""}`;
    if (tabletStartSyncedRef.current === syncKey) return;
    tabletStartSyncedRef.current = syncKey;
    void syncGuestTabletCheckIn(guestTabletWorkstationCode, "start", {
      bookingId: booking.id,
    });
  }, [open, booking?.id, guestTabletWorkstationCode]);

  useEffect(() => {
    if (!open || !guestTabletWorkstationCode) return;
    if (step === "summary") {
      void syncGuestTabletCheckIn(guestTabletWorkstationCode, "summary");
    } else if (step === "invoice" && invoiceId) {
      void syncGuestTabletCheckIn(guestTabletWorkstationCode, "invoice_prep", { invoiceId });
    }
  }, [open, step, guestTabletWorkstationCode, invoiceId]);

  useEffect(() => {
    const assigned = tabletSession.assignedPitchId;
    if (!waitingForGuestPitch || !assigned) return;
    if (assigned === guestPitchBaselineRef.current) return;
    setGuestChosenPitchId(assigned);
    setLocalBooking((prev) => (prev ? { ...prev, pitch_id: assigned } : prev));
  }, [tabletSession.assignedPitchId, waitingForGuestPitch]);

  useEffect(() => {
    if (open && step === "keys") {
      const t = window.setTimeout(() => transponderRef.current?.focus(), 50);
      return () => window.clearTimeout(t);
    }
  }, [open, step]);

  const loadInvoice = useCallback(() => {
    if (!invoiceId) return;
    setInvoiceAction("load");
    setError(null);
    api
      .get<Invoice>(`/api/v1/invoices/${invoiceId}`)
      .then(setInvoice)
      .catch((e: Error) => setError(e.message))
      .finally(() => setInvoiceAction(null));
  }, [invoiceId]);

  useEffect(() => {
    if (step === "invoice" && invoiceId) {
      loadInvoice();
    }
  }, [step, invoiceId, loadInvoice]);

  useEffect(() => {
    if (!pendingTabletPayment || step !== "invoice" || !invoiceId) return;
    loadInvoice();
  }, [pendingTabletPayment, step, invoiceId, loadInvoice]);

  function handleClose() {
    if (loading) return;
    const abortedDraft =
      checkedInBooking &&
      invoiceId &&
      invoice?.status === "draft";
    if (abortedDraft) {
      onDraftAborted?.(invoiceId, checkedInBooking);
      onCompleted(checkedInBooking);
    }
    void clearGuestTabletAfterCheckIn(guestTabletWorkstationCode);
    onClose();
  }

  async function performCheckIn() {
    if (!booking) return;
    setLoading(true);
    setError(null);
    try {
      const updated = await api.post<Booking>(`/api/v1/bookings/${booking.id}/check-in`, {
        transponder_id: accessKeysToApi(accessKeys).transponder_id,
        key_number: accessKeysToApi(accessKeys).key_number,
      });
      setCheckedInBooking(updated);
      await syncGuestTabletCheckIn(guestTabletWorkstationCode, "checked_in");
      if (updated.opened_invoice_id) {
        setInvoiceId(updated.opened_invoice_id);
        await syncGuestTabletCheckIn(guestTabletWorkstationCode, "invoice_prep", {
          invoiceId: updated.opened_invoice_id,
        });
        setStep("invoice");
      } else {
        await syncGuestTabletCheckIn(guestTabletWorkstationCode, "complete");
        onCompleted(updated);
        onClose();
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Check-in fehlgeschlagen.");
    } finally {
      setLoading(false);
    }
  }

  async function issueInvoice() {
    if (!invoice) return;
    setInvoiceAction("issue");
    setError(null);
    try {
      const issued = await api.post<Invoice>(`/api/v1/invoices/${invoice.id}/issue`, {});
      await syncGuestTabletCheckIn(guestTabletWorkstationCode, "invoice", {
        invoiceId: issued.id,
      });
      loadInvoice();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Rechnung konnte nicht ausgestellt werden.");
    } finally {
      setInvoiceAction(null);
    }
  }

  async function finishFlow() {
    const result = checkedInBooking;
    if (invoice && invoiceOpen <= 0 && isInvoiceFinalized(invoice.status)) {
      await syncGuestTabletCheckIn(guestTabletWorkstationCode, "complete");
    }
    if (result) {
      onCompleted(result);
    }
    onClose();
  }

  function handlePaymentRecorded() {
    loadInvoice();
    void tabletSession.reload();
    if (!invoiceId || !guestTabletWorkstationCode) return;
    void api.get<Invoice>(`/api/v1/invoices/${invoiceId}`).then((inv) => {
      const open = openAmountNumber(inv);
      if (open <= 0 && isInvoiceFinalized(inv.status)) {
        void syncGuestTabletCheckIn(guestTabletWorkstationCode, "complete");
      }
    });
  }

  async function confirmTabletPayment() {
    if (!guestTabletWorkstationCode || confirmingTabletPayment) return;
    setConfirmingTabletPayment(true);
    setError(null);
    try {
      await confirmGuestTabletPayment(guestTabletWorkstationCode);
      await tabletSession.reload();
      handlePaymentRecorded();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Zahlung konnte nicht bestätigt werden.");
    } finally {
      setConfirmingTabletPayment(false);
    }
  }

  function goToPitchStep() {
    void syncGuestTabletCheckIn(guestTabletWorkstationCode, "keys_done", {
      pitch:
        pitchAssignmentMode === "reception"
          ? { candidatePitchIds: [], skipReviewConfirmation: true }
          : undefined,
    });
    setStep("pitch");
  }

  const handlePitchChoicesChange = useCallback((choices: CheckInPitchChoice[]) => {
    setPitchChoices(choices);
  }, []);

  function handlePitchAssignmentModeChange(mode: CheckInPitchAssignmentMode) {
    setPitchAssignmentMode(mode);
    setError(null);
    void syncGuestTabletCheckIn(guestTabletWorkstationCode, "pitch", {
      pitch: {
        candidatePitchIds: [],
        skipReviewConfirmation: mode === "reception",
      },
    });
  }

  const guestDisplayName = guest
    ? `${guest.last_name}, ${guest.first_name}`
    : booking
      ? `Buchung ${booking.booking_number}`
      : "";

  const activeBooking = localBooking ?? booking;
  const offeredPitchChoices = pitchChoices
    .filter((choice) => selectedPitchIds.includes(choice.id))
    .sort((a, b) => a.code.localeCompare(b.code, "de"));
  const summaryBooking = checkedInBooking ?? activeBooking;
  const summaryPitchId = assignedPitchId ?? summaryBooking?.pitch_id ?? null;
  const summaryPitch = pitch && summaryPitchId && pitch.id === summaryPitchId ? pitch : null;
  const invoiceOpen = invoice ? openAmountNumber(invoice) : 0;

  async function assignPitchAtReception(pitchId: string) {
    if (!activeBooking) return;
    setPitchSubmit("reception");
    setLoading(true);
    setError(null);
    try {
      const updated = await api.post<Booking>(
        `/api/v1/bookings/${activeBooking.id}/assign-pitch`,
        { pitch_id: pitchId },
      );
      setLocalBooking(updated);
      setAssignedPitchId(pitchId);
      setSelectedPitchIds([pitchId]);
      setPitchAssignmentMode("reception");
      setReceptionCheckIn(true);
      setWaitingForGuestPitch(false);
      setGuestChosenPitchId(null);
      setReceptionPickId(null);
      guestPitchBaselineRef.current = null;
      await syncGuestTabletCheckIn(guestTabletWorkstationCode, "pitch", {
        pitch: {
          candidatePitchIds: [pitchId],
          assignedPitchId: pitchId,
          skipReviewConfirmation: true,
        },
      });
      setStep("summary");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Platzzuweisung fehlgeschlagen.");
    } finally {
      setPitchSubmit(null);
      setLoading(false);
    }
  }

  async function continueFromPitchStep() {
    if (!activeBooking || selectedPitchIds.length === 0) {
      setError("Bitte mindestens einen Stellplatz auswählen.");
      return;
    }
    if (pitchAssignmentMode === "reception") {
      if (selectedPitchIds.length !== 1) {
        setError("Für den Check-in an der Rezeption bitte genau einen Stellplatz auswählen.");
        return;
      }
      await assignPitchAtReception(selectedPitchIds[0]!);
      return;
    }
    setPitchSubmit("guest");
    setLoading(true);
    setError(null);
    try {
      if (selectedPitchIds.length === 1) {
        const pitchId = selectedPitchIds[0]!;
        const updated = await api.post<Booking>(
          `/api/v1/bookings/${activeBooking.id}/assign-pitch`,
          { pitch_id: pitchId },
        );
        setLocalBooking(updated);
        setAssignedPitchId(pitchId);
        setReceptionCheckIn(false);
        await syncGuestTabletCheckIn(guestTabletWorkstationCode, "pitch", {
          pitch: {
            candidatePitchIds: [pitchId],
            assignedPitchId: pitchId,
            skipReviewConfirmation: false,
          },
        });
        setStep("summary");
      } else {
        await syncGuestTabletCheckIn(guestTabletWorkstationCode, "pitch", {
          pitch: { candidatePitchIds: selectedPitchIds },
        });
        await tabletSession.reload();
        guestPitchBaselineRef.current = tabletSession.assignedPitchId;
        setGuestChosenPitchId(null);
        setReceptionPickId(null);
        setReceptionCheckIn(false);
        setWaitingForGuestPitch(true);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Platzzuweisung fehlgeschlagen.");
    } finally {
      setPitchSubmit(null);
      setLoading(false);
    }
  }

  async function continueAfterGuestPitchChoice() {
    if (!guestChosenPitchId) {
      setError("Der Gast hat noch keinen Platz gewählt.");
      return;
    }
    setPitchSubmit("guest");
    setLoading(true);
    setError(null);
    try {
      const updated = await api.post<Booking>(
        `/api/v1/bookings/${activeBooking!.id}/assign-pitch`,
        { pitch_id: guestChosenPitchId },
      );
      setLocalBooking(updated);
      setAssignedPitchId(guestChosenPitchId);
      setReceptionCheckIn(false);
      await syncGuestTabletCheckIn(guestTabletWorkstationCode, "pitch", {
        pitch: {
          candidatePitchIds: selectedPitchIds,
          assignedPitchId: guestChosenPitchId,
          skipReviewConfirmation: false,
        },
      });
      setWaitingForGuestPitch(false);
      setStep("summary");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Platzzuweisung fehlgeschlagen.");
    } finally {
      setPitchSubmit(null);
      setLoading(false);
    }
  }

  const titleByStep: Record<FlowStep, string> = {
    keys: "Check-in · Zugang",
    pitch: "Check-in · Platz wählen",
    summary: "Check-in · Übersicht",
    invoice: "Check-in · Rechnung & Zahlung",
  };

  const descriptionByStep: Record<FlowStep, string> = {
    keys: `${guestDisplayName} · Transponder scannen oder Schlüsselnummer eingeben`,
    pitch: `${guestDisplayName} · Freie Plätze auf dem Plan auswählen`,
    summary: `${guestDisplayName} · Daten dem Gast vorlesen`,
    invoice: "Rechnungsentwurf prüfen, ausstellen und Zahlung entgegennehmen",
  };

  if (!booking) return null;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={titleByStep[step]}
      description={descriptionByStep[step]}
      size={step === "invoice" || step === "pitch" ? "lg" : "md"}
    >
      <nav aria-label="Check-in-Fortschritt" className="mb-5">
        <ol className="flex items-center justify-between gap-1">
          {visibleSteps.map((s, index) => {
            const stepIndex = visibleSteps.findIndex((x) => x.id === step);
            const currentIndex = visibleSteps.findIndex((x) => x.id === s.id);
            const done = currentIndex < stepIndex;
            const active = s.id === step;
            return (
              <li key={s.id} className="flex min-w-0 flex-1 flex-col items-center gap-1">
                <span
                  className={clsx(
                    "flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold",
                    done && "bg-accent text-label-on-accent",
                    active && !done && "bg-accent text-label-on-accent ring-2 ring-accent/30",
                    !done && !active && "bg-fill-tertiary text-label-secondary",
                  )}
                  aria-current={active ? "step" : undefined}
                >
                  {done ? "✓" : index + 1}
                </span>
                <span
                  className={clsx(
                    "max-w-full truncate text-center text-[0.65rem] font-medium sm:text-xs",
                    active ? "text-label" : "text-label-secondary",
                  )}
                >
                  {s.label}
                </span>
              </li>
            );
          })}
        </ol>
      </nav>

      {error ? <p className="mb-4 text-sm text-status-red">{error}</p> : null}

      {step === "keys" ? (
        <div className="space-y-4">
          <div className="rounded-xl border border-separator bg-fill-tertiary/40 p-4">
            <div className="mb-3 flex items-center gap-2 text-sm text-label-secondary">
              <Radio className="h-4 w-4 shrink-0" />
              <KeyRound className="h-4 w-4 shrink-0" />
              <span>Transponder scannen oder Schlüsselnummer eingeben</span>
            </div>
            <AccessKeyFields
              values={accessKeys}
              onChange={setAccessKeys}
              transponderInputRef={transponderRef}
              onEnterKey={goToPitchStep}
            />
            <p className="mt-2 text-xs text-label-tertiary">
              Optional – Enter springt zur Platzauswahl. Beides kann leer bleiben.
            </p>
          </div>
          <div className="flex flex-wrap justify-end gap-2 border-t border-separator pt-4">
            <Button type="button" variant="ghost" onClick={handleClose}>
              Abbrechen
            </Button>
            <Button type="button" onClick={goToPitchStep}>
              Weiter
            </Button>
          </div>
        </div>
      ) : null}

      {step === "pitch" && activeBooking ? (
        <div className="space-y-4">
          {waitingForGuestPitch ? (
            <div className="space-y-3">
              <div className="rounded-xl border border-accent/25 bg-accent-soft/40 p-4 text-sm text-label">
                <p className="font-medium">Warten auf Gastwahl am Tablet</p>
                <p className="mt-1 text-label-secondary">
                  Der Gast wählt einen von {selectedPitchIds.length} angebotenen Plätzen. Sobald die
                  Auswahl erfolgt ist, können Sie fortfahren.
                </p>
                {guestChosenPitchId ? (
                  <p className="mt-2 text-status-green">Platz wurde vom Gast gewählt.</p>
                ) : null}
              </div>
              <div className="rounded-xl border border-separator p-4 text-sm text-label">
                <p className="font-medium">Ohne Gast am Tablet fortfahren</p>
                <p className="mt-1 text-label-secondary">
                  Wählen Sie einen der angebotenen Plätze. Der Check-in läuft danach vollständig an
                  der Rezeption, ohne Bestätigung am Tablet.
                </p>
                <ul className="mt-3 max-h-48 space-y-1 overflow-y-auto">
                  {offeredPitchChoices.map((choice) => {
                    const selected = receptionPickId === choice.id;
                    return (
                      <li key={choice.id}>
                        <button
                          type="button"
                          className={clsx(
                            "flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm",
                            selected
                              ? "bg-accent text-label-on-accent"
                              : "hover:bg-fill-tertiary text-label",
                          )}
                          aria-pressed={selected}
                          onClick={() => setReceptionPickId(selected ? null : choice.id)}
                        >
                          <span className="font-medium">{choice.code}</span>
                          {choice.label ? (
                            <span
                              className={clsx(
                                "text-xs",
                                selected ? "opacity-90" : "text-label-secondary",
                              )}
                            >
                              {choice.label}
                            </span>
                          ) : null}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          ) : (
            <CheckInPitchSelectionStep
              booking={activeBooking}
              selectedPitchIds={selectedPitchIds}
              onSelectedPitchIdsChange={setSelectedPitchIds}
              assignmentMode={pitchAssignmentMode}
              onAssignmentModeChange={handlePitchAssignmentModeChange}
              onPitchChoicesChange={handlePitchChoicesChange}
            />
          )}
          <div className="flex flex-wrap justify-end gap-2 border-t border-separator pt-4">
            <Button
              type="button"
              variant="ghost"
              disabled={loading}
              onClick={() => {
                if (waitingForGuestPitch) {
                  setWaitingForGuestPitch(false);
                  setGuestChosenPitchId(null);
                  guestPitchBaselineRef.current = null;
                } else {
                  setStep("keys");
                }
              }}
            >
              Zurück
            </Button>
            <Button type="button" variant="secondary" disabled={loading} onClick={handleClose}>
              Abbrechen
            </Button>
            {waitingForGuestPitch ? (
              <>
                <Button
                  type="button"
                  variant={guestChosenPitchId ? "secondary" : "primary"}
                  loading={pitchSubmit === "reception"}
                  disabled={!receptionPickId || loading}
                  onClick={() => {
                    if (receptionPickId) void assignPitchAtReception(receptionPickId);
                  }}
                >
                  An der Rezeption zuweisen
                </Button>
                <Button
                  type="button"
                  variant={guestChosenPitchId ? "primary" : "secondary"}
                  loading={pitchSubmit === "guest"}
                  disabled={!guestChosenPitchId || loading}
                  onClick={() => void continueAfterGuestPitchChoice()}
                >
                  Weiter zur Übersicht
                </Button>
              </>
            ) : (
              <Button
                type="button"
                loading={loading}
                disabled={
                  pitchAssignmentMode === "reception"
                    ? selectedPitchIds.length !== 1
                    : selectedPitchIds.length === 0
                }
                onClick={() => void continueFromPitchStep()}
              >
                Weiter
              </Button>
            )}
          </div>
        </div>
      ) : null}

      {step === "summary" && summaryBooking ? (
        <div className="space-y-4">
          {summaryPitchId ? (
            <CheckInPitchSummaryMap
              pitchId={summaryPitchId}
              referenceDate={summaryBooking.arrival_date}
            />
          ) : null}
          <GuestStaySummary
            booking={summaryBooking}
            guest={guest}
            pitch={summaryPitch}
            accessKeys={accessKeys}
          />
          {guestTabletWorkstationCode ? (
            <>
              {receptionCheckIn ? (
                <p className="rounded-lg border border-separator bg-fill-tertiary/40 px-3 py-2 text-sm text-label-secondary">
                  Check-in an der Rezeption. Der Gast muss am Tablet nichts bestätigen.
                </p>
              ) : guestDataConfirmed ? (
                <p className="rounded-lg border border-status-green/30 bg-status-green/10 px-3 py-2 text-sm text-status-green">
                  Der Gast hat die persönlichen Daten am Tablet bestätigt.
                </p>
              ) : bypassGuestDataConfirmation ? (
                <p className="rounded-lg border border-status-orange/40 bg-status-orange/10 px-3 py-2 text-sm text-status-orange">
                  Sie fahren ohne Bestätigung der Gastdaten am Tablet fort. Der normale
                  Check-in-Ablauf wird umgangen.
                </p>
              ) : (
                <div className="space-y-2 rounded-lg border border-accent/20 bg-accent-soft/50 px-3 py-2">
                  <p className="text-sm text-label-secondary">
                    Warten auf Bestätigung der Gastdaten am Tablet…
                  </p>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    disabled={loading}
                    onClick={() => setBypassGuestDataConfirmation(true)}
                  >
                    Ohne Tablet-Bestätigung fortfahren
                  </Button>
                </div>
              )}
            </>
          ) : null}
          {expectsInvoiceStep ? (
            <p className="rounded-lg border border-accent/20 bg-accent-soft/50 px-3 py-2 text-sm text-label-secondary">
              Nach dem Check-in folgt die Touristen-Vorkasse (Vollrechnungsentwurf mit allen
              Positionen und Zahlung).
            </p>
          ) : null}
          <div className="flex flex-wrap justify-end gap-2 border-t border-separator pt-4">
            <Button
              type="button"
              variant="ghost"
              disabled={loading}
              onClick={() => {
                setReceptionCheckIn(false);
                setStep("pitch");
              }}
            >
              Zurück
            </Button>
            <Button type="button" variant="secondary" disabled={loading} onClick={handleClose}>
              Abbrechen
            </Button>
            <Button
              type="button"
              loading={loading}
              disabled={
                guestTabletWorkstationCode
                  ? !receptionCheckIn && !guestDataConfirmed && !bypassGuestDataConfirmation
                  : false
              }
              onClick={() => void performCheckIn()}
            >
              Check-in durchführen
            </Button>
          </div>
        </div>
      ) : null}

      {step === "invoice" && invoiceId ? (
        <div className="space-y-4">
          {invoice ? (
            <AddInvoiceLineModal
              open={addLineOpen}
              invoice={invoice}
              onClose={() => setAddLineOpen(false)}
              onAdded={() => {
                loadInvoice();
              }}
            />
          ) : null}

          {invoiceAction === "load" && !invoice ? (
            <p className="text-sm text-label-secondary">Rechnung wird geladen…</p>
          ) : null}

          {invoice ? (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <InvoiceTypeBadge invoiceType={invoice.invoice_type} />
                <StatusBadge status={invoice.status} kind="invoice" />
                <span className="tabular-nums text-sm font-medium text-label">
                  {formatCurrency(invoice.total_amount, invoice.currency)}
                </span>
              </div>

              <InvoiceLinesEditor
                invoice={invoice}
                booking={booking}
                disabled={!!invoiceAction}
                onChanged={() => loadInvoice()}
              />

              {canInvoice && canAddInvoiceLine(invoice) ? (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={!!invoiceAction}
                  onClick={() => setAddLineOpen(true)}
                >
                  Position hinzufügen
                </Button>
              ) : null}

              {canInvoice && invoice.status === "draft" ? (
                <div className="border-t border-separator pt-3">
                  <Button
                    type="button"
                    loading={invoiceAction === "issue"}
                    disabled={!!invoiceAction}
                    onClick={() => void issueInvoice()}
                  >
                    Rechnung ausstellen (ohne Zahlung)
                  </Button>
                </div>
              ) : null}

              <InvoiceQuickPaymentPanel
                invoice={invoice}
                guestEmail={guest?.email ?? null}
                canRecord={canInvoice}
                disabled={!!invoiceAction}
                onPaymentComplete={() => {
                  handlePaymentRecorded();
                }}
              />

              <InvoicePaymentsSection
                invoice={invoice}
                canRecord={canInvoice}
                onPaymentRecorded={handlePaymentRecorded}
                pendingTabletPayment={pendingTabletPayment}
                confirmingTabletPayment={confirmingTabletPayment}
                onConfirmTabletPayment={
                  pendingTabletPayment ? () => void confirmTabletPayment() : undefined
                }
              />

              {invoice.status === "draft" ? (
                <p className="text-xs text-label-tertiary">
                  Rechnung zuerst ausstellen, danach Zahlung erfassen.
                </p>
              ) : null}
            </>
          ) : null}

          <div className="flex flex-col items-end gap-2 border-t border-separator pt-4">
            {invoice && invoiceOpen > 0 && isInvoiceFinalized(invoice.status) ? (
              <p className="text-xs text-status-orange">
                Noch {formatCurrency(String(invoiceOpen), invoice.currency)} offen – bitte Zahlung erfassen oder abschließen.
              </p>
            ) : null}
            <Button
              type="button"
              variant="primary"
              disabled={
                !!invoiceAction ||
                Boolean(invoice?.status === "draft") ||
                Boolean(
                  invoice &&
                    invoiceOpen > 0 &&
                    isInvoiceFinalized(invoice.status),
                )
              }
              onClick={() => void finishFlow()}
            >
              Check-in abschließen
            </Button>
            {invoice?.status === "draft" ? (
              <p className="text-xs text-status-amber">
                Bitte Rechnung zuerst ausstellen und ggf. Zahlung erfassen, oder den Dialog
                schließen und später mit „Rechnung fortsetzen“ weitermachen.
              </p>
            ) : null}
          </div>
        </div>
      ) : null}
    </Modal>
  );
}
